import * as React from 'react';
import { ChevronDown, Search, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar, ScrollAreaThumb } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useDataSource } from '@/contexts/DataSourceContext';
import type { DataMode } from '@/contexts/DataSourceContext';
import { getRoleService } from '@/services/roleService';
import { RoleSearchModal } from './RoleSearchModal';
import type { RoleListItem } from '@/types/role';

const INITIAL_DISPLAY_COUNT = 10;

interface RoleSelectorProps {
  value: number | undefined;
  onChange: (roleId: number | undefined, roleName?: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  screenMode?: DataMode;
  displayName?: string;
  isSubmitted?: boolean;
}

export function RoleSelector({
  value,
  onChange,
  disabled = false,
  required = false,
  placeholder = 'Select role...',
  className,
  screenMode: screenModeProp,
  displayName,
  isSubmitted = false,
}: RoleSelectorProps) {
  const { getScreenMode } = useDataSource();
  const [roles, setRoles] = React.useState<RoleListItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Use the passed screenMode prop, or fall back to roles-rates screen mode
  const effectiveMode = screenModeProp ?? getScreenMode('roles-rates');

  const roleService = React.useMemo(() => getRoleService(effectiveMode), [effectiveMode]);

  React.useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      try {
        const activeRoles = await roleService.getActiveRoles();
        setRoles(activeRoles);
      } catch (error) {
        console.error('Failed to fetch roles:', error);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, [roleService]);

  const selectedRole = React.useMemo(
    () => roles.find((r) => r.id === value),
    [roles, value]
  );

  // Filter roles based on search query
  const filteredRoles = React.useMemo(() => {
    if (!searchQuery.trim()) return roles;

    const query = searchQuery.toLowerCase();
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(query) ||
        (role.description && role.description.toLowerCase().includes(query))
    );
  }, [roles, searchQuery]);

  // Show initial set of roles, with "Show More" for the rest
  const displayedRoles = React.useMemo(() => {
    return filteredRoles.slice(0, INITIAL_DISPLAY_COUNT);
  }, [filteredRoles]);

  const hasMoreRoles = filteredRoles.length > INITIAL_DISPLAY_COUNT;

  // Handle role selection
  const handleSelectRole = React.useCallback(
    (roleId: number | undefined) => {
      const role = roles.find((r) => r.id === roleId);
      onChange(roleId, role?.name);
      setDropdownOpen(false);
    },
    [onChange, roles]
  );

  // Handle modal open
  const handleOpenModal = React.useCallback(() => {
    setModalOpen(true);
    setDropdownOpen(false);
    setSearchQuery('');
  }, []);

  // Reset search when dropdown closes
  React.useEffect(() => {
    if (!dropdownOpen) {
      setSearchQuery('');
    } else {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [dropdownOpen]);

  return (
    <>
      <Popover open={!disabled && dropdownOpen} onOpenChange={(open) => !disabled && setDropdownOpen(open)}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              value={selectedRole ? selectedRole.name : (value && displayName ? displayName : '')}
              placeholder={isSubmitted ? '' : placeholder}
              readOnly
              disabled={disabled}
              className={cn(
                'cursor-pointer pr-10',
                required && !value && !isSubmitted && 'border-destructive focus:ring-destructive',
                disabled && 'cursor-not-allowed',
                className
              )}
              onClick={() => !disabled && setDropdownOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  !disabled && setDropdownOpen(true);
                }
              }}
              aria-label="Select role"
              aria-expanded={dropdownOpen}
              role="combobox"
            />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '250px' }}
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-8 h-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <ScrollArea className="max-h-[300px]">
            <ScrollAreaViewport className="max-h-[300px] p-2">
              {loading ? (
                <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                  <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin opacity-50" />
                  <p>Loading roles...</p>
                </div>
              ) : (
                <>
                  {/* No role option */}
                  {!required && (
                    <button
                      onClick={() => handleSelectRole(undefined)}
                      className={cn(
                        'w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left',
                        !value && 'bg-accent'
                      )}
                    >
                      <span className="text-muted-foreground">No role selected</span>
                    </button>
                  )}

                  {/* Role items */}
                  {displayedRoles.length > 0 && (
                    <div className="space-y-0.5">
                      {displayedRoles.map((role) => (
                        <button
                          key={role.id}
                          onClick={() => handleSelectRole(role.id)}
                          className={cn(
                            'w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left',
                            value === role.id && 'bg-accent'
                          )}
                        >
                          <span className="flex-1 truncate">
                            {role.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* No Results Message */}
                  {searchQuery && filteredRoles.length === 0 && (
                    <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                      <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No roles found matching &quot;{searchQuery}&quot;</p>
                      <p className="text-xs mt-1">Try a different search term</p>
                    </div>
                  )}

                  {/* Empty State (no roles at all) */}
                  {!searchQuery && roles.length === 0 && (
                    <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                      <p>No roles available</p>
                    </div>
                  )}
                </>
              )}
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical">
              <ScrollAreaThumb />
            </ScrollAreaScrollbar>
          </ScrollArea>

          {/* Show More button */}
          {(hasMoreRoles || roles.length > INITIAL_DISPLAY_COUNT) && (
            <>
              <Separator className="my-0" />
              <div className="p-2">
                <Button
                  variant="ghost"
                  className="w-full justify-center text-sm"
                  onClick={handleOpenModal}
                >
                  Show More...
                </Button>
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>

      {/* Full Search Modal */}
      <RoleSearchModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        roles={roles}
        value={value}
        onSelect={(roleId) => {
          const role = roles.find((r) => r.id === roleId);
          onChange(roleId, role?.name);
        }}
        loading={loading}
        required={required}
      />
    </>
  );
}
